/* eslint-disable react-refresh/only-export-components -- co-localizamos
   el estado + constantes con el componente; HMR no crítico acá. */
/**
 * Controles de filtrado del Radar (Sprint 3).
 *
 * Maneja 3 filtros:
 * - `search` — texto libre. Debounce interno de 300ms para no bombardear
 *   el backend en cada tecla.
 * - `sector` — dropdown con los sectores canónicos (`KAPTAR_SECTOR_OPTIONS`).
 * - `maxDaysToClose` — chips exclusivos (todas / 7 días / 30 días).
 *
 * Estado controlado: el parent (`Radar.tsx`) es source of truth. Este
 * componente solo dispara `onChange` con el FilterState completo cada vez
 * que algo cambia.
 */
import { useEffect, useState } from "react";

import { KAPTAR_SECTOR_OPTIONS } from "@/lib/api/projects";
import { RADAR_CURATION_ACTIONS_ENABLED } from "@/lib/features";

export interface RadarFilterState {
  search: string;
  sector: string; // "" = sin filtro
  /** undefined = sin filtro; 7 | 30 activos. */
  maxDaysToClose: number | undefined;
  /** Sprint 8.5: solo favoritas del usuario actual. */
  onlyFavorited: boolean;
  /** Sprint 8.5: incluir descartadas en el listado (default ocultas). */
  includeDismissed: boolean;
  // Sprint 19 UX-1 (mayo 21, 2026): el filtro `onlyFeatured` se
  // eliminó junto con el concepto de "Destacada".
}

export const EMPTY_FILTERS: RadarFilterState = {
  search: "",
  sector: "",
  maxDaysToClose: undefined,
  onlyFavorited: false,
  includeDismissed: false,
};

// Issue #728: el backend filtra por igualdad exacta, así que las opciones
// son el vocabulario canónico (el mismo que escriben los scrapers y que
// usan los enlaces `/radar?sector=X`), no una lista propia. Si llega por
// enlace un sector fuera de esa lista, se agrega como opción para que el
// selector muestre lo que de verdad está filtrando el listado.
function sectorOptions(current: string): { value: string; label: string }[] {
  if (current === "" || KAPTAR_SECTOR_OPTIONS.some((s) => s.value === current)) {
    return KAPTAR_SECTOR_OPTIONS;
  }
  return [...KAPTAR_SECTOR_OPTIONS, { value: current, label: current.replace(/_/g, " ") }];
}

const URGENCY_CHIPS: { value: number | undefined; label: string }[] = [
  { value: undefined, label: "Todas" },
  { value: 7, label: "Cierran en 7 días" },
  { value: 30, label: "Cierran en 30 días" },
];

export interface RadarFiltersProps {
  value: RadarFilterState;
  onChange: (next: RadarFilterState) => void;
  /** Total de resultados actuales (para "Mostrando X resultados"). */
  totalResults?: number;
}

export function RadarFilters({ value, onChange, totalResults }: RadarFiltersProps) {
  // Debounce del search: mantengo `draft` local y propago después de 300ms
  // sin cambios. Así el usuario puede tipear sin firing de fetches por tecla.
  const [searchDraft, setSearchDraft] = useState(value.search);

  // Sync externo → draft (ej: click en "Limpiar filtros" resetea). Se ajusta
  // durante el render: en un efecto, el input mostraba el texto viejo por un
  // frame después de limpiar.
  const [prevSearch, setPrevSearch] = useState(value.search);
  if (prevSearch !== value.search) {
    setPrevSearch(value.search);
    setSearchDraft(value.search);
  }

  useEffect(() => {
    if (searchDraft === value.search) return;
    const t = setTimeout(() => {
      onChange({ ...value, search: searchDraft });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft]);

  const hasActiveFilters =
    value.search !== "" ||
    value.sector !== "" ||
    value.maxDaysToClose !== undefined ||
    value.onlyFavorited ||
    value.includeDismissed;

  return (
    <section
      aria-label="Filtros del radar"
      className="mb-4 rounded-xl border border-slate-200 bg-white p-4 shadow-xs"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        {/* Search — full width en mobile, flex-1 en sm+ */}
        <div className="w-full sm:min-w-[220px] sm:flex-1">
          <label htmlFor="radar-search" className="mb-1 block text-xs font-semibold text-slate-700">
            Buscar
          </label>
          <input
            id="radar-search"
            type="search"
            placeholder="Título o descripción..."
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-hidden"
          />
        </div>

        {/* Sector */}
        <div>
          <label htmlFor="radar-sector" className="mb-1 block text-xs font-semibold text-slate-700">
            Sector
          </label>
          <select
            id="radar-sector"
            value={value.sector}
            onChange={(e) => onChange({ ...value, sector: e.target.value })}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-hidden"
          >
            <option value="">Todos</option>
            {sectorOptions(value.sector).map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        {/* Urgencia */}
        <div>
          <span className="mb-1 block text-xs font-semibold text-slate-700">Urgencia</span>
          <div role="radiogroup" aria-label="Urgencia" className="flex gap-1.5">
            {URGENCY_CHIPS.map((chip) => {
              const active = chip.value === value.maxDaysToClose;
              return (
                <button
                  key={chip.label}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => onChange({ ...value, maxDaysToClose: chip.value })}
                  className={
                    "rounded-full px-3 py-1 text-xs font-semibold transition " +
                    (active
                      ? "bg-brand-700 text-white"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200")
                  }
                >
                  {chip.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Clear + count */}
        <div className="flex items-center justify-between gap-3 sm:ml-auto sm:justify-start">
          {totalResults !== undefined && (
            <span className="text-xs text-slate-500" aria-live="polite">
              {totalResults} {totalResults === 1 ? "resultado" : "resultados"}
            </span>
          )}
          {hasActiveFilters && (
            <button
              type="button"
              onClick={() => onChange(EMPTY_FILTERS)}
              className="text-xs font-semibold text-brand-700 hover:underline"
            >
              Limpiar filtros
            </button>
          )}
        </div>
      </div>

      {/* ---- Toggles secundarios (Sprint 8.5) ----
        Diseño: chips toggleables, no checkboxes — visualmente más cerca
        del filtro de urgencia y del lenguaje de "etiquetas". Cada toggle
        es independiente.

        Sprint 10 · 5: movidos a V2 detrás de
        RADAR_CURATION_ACTIONS_ENABLED. Los chips dependen de las
        acciones de favoritar/descartar/curar — sin esas acciones no
        tiene sentido mostrarlos. */}
      {RADAR_CURATION_ACTIONS_ENABLED && (
        <div className="mt-3 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
          {/* Sprint 19 UX-1 (mayo 21, 2026): chip "★ Destacadas" eliminado
              junto con el concepto. Los chips de favoritas y descartadas
              se mantienen — son curaduría del usuario, no del equipo. */}
          <ToggleChip
            label="♥ Favoritas"
            active={value.onlyFavorited}
            onClick={() => onChange({ ...value, onlyFavorited: !value.onlyFavorited })}
            testid="filter-toggle-favorited"
          />
          <ToggleChip
            label="Mostrar descartadas"
            active={value.includeDismissed}
            onClick={() => onChange({ ...value, includeDismissed: !value.includeDismissed })}
            testid="filter-toggle-dismissed"
          />
        </div>
      )}
    </section>
  );
}

function ToggleChip({
  label,
  active,
  onClick,
  testid,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  testid: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      data-active={active}
      aria-pressed={active}
      className={
        "rounded-full px-3 py-1 text-xs font-semibold transition " +
        (active ? "bg-brand-700 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200")
      }
    >
      {label}
    </button>
  );
}
